// "Biblioteca de Provas" — feature #11 (renomeada de "Descobrir bancos").
// Lista bancos com visibility='public' organizados em pastas curadas pelo
// platform admin. Aluno importa criando cópia privada no seu acervo, com
// opção de escolher uma pasta pessoal de destino.
//
// Comportamentos importantes ([[project-biblioteca-de-provas]]):
//   - Anti-duplicação: se o aluno já importou esse banco, mostra badge
//     "Já importado" + link "Abrir no meu acervo" em vez de botão de import.
//   - Folder picker: ao importar, perguntamos em qual pasta pessoal (se
//     existir) colocar. Default = "Sem pasta".
//   - Filtro por pasta da biblioteca (chips no topo, badge com contagem).

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  ArrowRight,
  Calendar,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Database,
  Download,
  Eye,
  FolderClosed,
  FolderOpen,
  LayoutGrid,
  Library,
  List,
  Loader2,
  PenSquare,
  Search,
  Sparkles,
  Tag,
  X
} from "lucide-react";
import { call } from "./api";
import { friendlyError } from "@app/ui/lib/friendly-error";
import { activeNav, loadBanks } from "./App";
import { activeSessionId } from "./AiStudyView";
import { bankSearchQuery, foldersData, loadFolders } from "./BankFolders";
import { openBank } from "./BankDetailView";
import { QuestionPreview, type PreviewQuestion } from "@app/ui/question-preview";

interface DiscoverBankRow {
  id: string;
  name: string;
  description: string | null;
  tags: string[];
  ownerName: string | null;
  libraryFolderId: string | null;
  questionCount: number;
  mcCount?: number;
  essayCount?: number;
  /** Dissertativas paralelas (geradas por IA). 0 = sem versão. */
  dissertativeCount?: number;
  updatedAt: string;
  alreadyImported: boolean;
  myCopyId: string | null;
  /** True quando o aluno já tem o banco visível no /banks (por owner direto,
   *  por membership na company alvo + isPublicToCompany, ou por grant
   *  individual). Nesse caso, UI esconde "Importar pro meu acervo" e mostra
   *  "Já no seu acervo · Abrir" — abrir direto pelo id original. */
  alreadyAccessible: boolean;
  accessReason: "owner" | "company" | "grant" | null;
}
interface LibraryFolderRow {
  id: string;
  name: string;
  slug: string;
  parentId: string | null;
  position: number;
  bankCount: number;
  essayCount: number;
}
interface DiscoverEssayRow {
  id: string;
  slug: string;
  title: string;
  displayName: string;
  banca: string;
  year: number | null;
  phase: string | null;
  edition: string | null;
  applicationDate: string | null;
  examEditionKey: string | null;
  libraryFolderId: string | null;
  originalSourceUrl: string | null;
  updatedAt: string;
}
interface DiscoverResponse {
  items: DiscoverBankRow[];
  page: number;
  pageSize: number;
  hasMore: boolean;
  topics: Array<{ topic: string; count: number }>;
  folders: LibraryFolderRow[];
  essays: DiscoverEssayRow[];
}

const items = signal<DiscoverBankRow[]>([]);
const essays = signal<DiscoverEssayRow[]>([]);
const topics = signal<Array<{ topic: string; count: number }>>([]);
// Modo de visualização — persistido em localStorage. "grid" (cards 3-up) ou
// "list" (linhas compactas). Mantém o padrão de paridade com Meu Acervo.
// [[project-student-acervo-library-design-parity]]
type DiscoverViewMode = "grid" | "list";
function readViewMode(): DiscoverViewMode {
  try {
    const raw = window.localStorage.getItem("student.library.viewMode");
    if (raw === "list") return "list";
  } catch {/* ignore */}
  return "grid";
}
const viewMode = signal<DiscoverViewMode>(readViewMode());
function setViewMode(m: DiscoverViewMode) {
  viewMode.value = m;
  try {
    window.localStorage.setItem("student.library.viewMode", m);
  } catch {/* ignore */}
}
const libraryFolders = signal<LibraryFolderRow[]>([]);
const phase = signal<"idle" | "loading" | "ready" | "error">("idle");
const errMsg = signal<string | null>(null);
const hasMore = signal<boolean>(false);
const page = signal<number>(1);
const activeTopic = signal<string | null>(null);
/** Filtro de pasta da biblioteca: null = "Todas", string = folder id, "no-folder" = sem categoria. */
const activeLibraryFolder = signal<string | null | "no-folder">(null);
/** Tópicos ficam escondidos por padrão — só aparecem quando o aluno decide
 *  filtrar por algum. Pedido 2026-05-19: a Biblioteca não deve mostrar tantos
 *  tópicos de cara. */
const topicsExpanded = signal<boolean>(false);
/** Pastas expandidas na árvore lateral (persistido em localStorage). */
const treeExpanded = signal<Set<string>>(
  new Set(
    (() => {
      try {
        const raw = window.localStorage.getItem("student.library.tree.expanded");
        if (raw) return JSON.parse(raw) as string[];
      } catch {}
      return [];
    })()
  )
);
function persistTreeExpanded() {
  try {
    window.localStorage.setItem(
      "student.library.tree.expanded",
      JSON.stringify(Array.from(treeExpanded.value))
    );
  } catch {}
}

async function loadDiscover(opts: { append?: boolean } = {}) {
  if (!opts.append) {
    phase.value = "loading";
    page.value = 1;
  }
  errMsg.value = null;
  try {
    const params = new URLSearchParams();
    const q = bankSearchQuery.value.trim();
    if (q) params.set("q", q);
    if (activeTopic.value) params.set("topic", activeTopic.value);
    if (activeLibraryFolder.value !== null) {
      params.set("folderId", String(activeLibraryFolder.value));
    }
    params.set("page", String(page.value));
    const res = await call<DiscoverResponse>(`/api/student/discover/banks?${params.toString()}`);
    items.value = opts.append ? [...items.value, ...res.items] : res.items;
    // Redações da pasta — só atualizam na primeira página (não paginadas)
    if (!opts.append) essays.value = res.essays ?? [];
    topics.value = res.topics;
    libraryFolders.value = res.folders;
    hasMore.value = res.hasMore;
    phase.value = "ready";
  } catch (err) {
    errMsg.value = friendlyError(err);
    phase.value = "error";
  }
}

export function DiscoverBanksView() {
  // Reload quando termo de busca, topic ou pasta mudam — debounce via setTimeout.
  useEffect(() => {
    const t = setTimeout(() => void loadDiscover(), 250);
    // Pré-carrega as bank-folders pessoais pra o folder picker do import.
    void loadFolders({ silent: true });
    return () => clearTimeout(t);
  }, [bankSearchQuery.value, activeTopic.value, activeLibraryFolder.value]);

  return (
    <div class="space-y-4">
      <header class="space-y-1">
        <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <Library className="size-6 text-primary" />
          Biblioteca de provas
        </h1>
        <p class="text-sm text-muted-foreground">
          Catálogo curado da plataforma. Importe para o seu acervo para estudar,
          simular prova e exportar PDF normalmente. A cópia é sua — edições no
          original não afetam o que você importou.
        </p>
      </header>

      {/* Toolbar sticky — busca + toggle Grid/Lista. Fica visível durante o
          scroll pra aluno trocar de modo / buscar sem voltar ao topo. */}
      <div class="sticky top-0 z-20 -mx-1 flex flex-wrap items-center gap-2 rounded-md border border-border bg-card/95 px-2 py-2 backdrop-blur-sm">
        <div class="min-w-0 flex-1">
          <SearchBox />
        </div>
        <div
          class="inline-flex shrink-0 items-center gap-0.5 rounded-md border border-border bg-background p-0.5"
          role="group"
          aria-label="Modo de visualização"
        >
          <button
            type="button"
            onClick={() => setViewMode("grid")}
            title="Grid de cards"
            aria-label="Modo grid"
            aria-pressed={viewMode.value === "grid" ? "true" : "false"}
            class={`inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-semibold transition-colors ${
              viewMode.value === "grid"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <LayoutGrid className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setViewMode("list")}
            title="Lista compacta"
            aria-label="Modo lista"
            aria-pressed={viewMode.value === "list" ? "true" : "false"}
            class={`inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-semibold transition-colors ${
              viewMode.value === "list"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <List className="size-3.5" />
          </button>
        </div>
      </div>

      {/* Layout File Explorer: árvore de pastas à esquerda + grid de bancos à
          direita. Mobile (sm-): empilha. Desktop (md+): 2 colunas. */}
      <div class="grid grid-cols-1 gap-4 md:grid-cols-[240px_1fr]">
        <FolderTreeAside />
        <div class="space-y-3">
          <TopicsSection />

          {phase.value === "loading" && items.value.length === 0 && (
            <p class="flex items-center gap-2 py-12 text-center text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Carregando catálogo…
            </p>
          )}

          {phase.value === "error" && (
            <div class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              <AlertCircle className="mr-2 inline size-4" />
              {errMsg.value ?? "Falha ao carregar."}
            </div>
          )}

          {/* Subpastas da pasta ativa — aparecem como cards no topo do painel
              direito, antes dos bancos. Replica a UX de File Explorer do admin
              [[project-student-acervo-library-design-parity]] */}
          <SubfoldersGrid />

          {phase.value === "ready" && items.value.length === 0 && essays.value.length === 0 && (
            <DiscoverEmptyState />
          )}

          {items.value.length > 0 &&
            (viewMode.value === "list" ? (
              <ul class="flex flex-col gap-1.5">
                {items.value.map((b) => (
                  <DiscoverListRow key={b.id} bank={b} />
                ))}
              </ul>
            ) : (
              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {items.value.map((b) => (
                  <DiscoverCard key={b.id} bank={b} />
                ))}
              </div>
            ))}

          {/* Redações desta pasta — seção própria, lista compacta. Card linka
              pro fluxo "Estudar com IA > Nova Redação" passando theme id. */}
          <DiscoverEssaysSection />
        </div>
      </div>

      {hasMore.value && phase.value === "ready" && (
        <div class="flex justify-center pt-2">
          <button
            type="button"
            onClick={() => {
              page.value += 1;
              void loadDiscover({ append: true });
            }}
            className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
          >
            Carregar mais
          </button>
        </div>
      )}
    </div>
  );
}

/** Seção que renderiza as redações da pasta ativa (ou todas quando sem filtro).
 *  Aparece DEPOIS dos cards de bancos. Click no card aplica o tema (cria sessão
 *  ai_study kind=essay) e navega pro "Estudar com IA" com a sessão aberta —
 *  mesmo backend que o EssayLibraryPicker usa, sem mostrar o caderno antes.
 *  Pra preview do caderno, o aluno pode usar o picker normal em "Nova Redação". */
function DiscoverEssaysSection() {
  const list = essays.value;
  if (list.length === 0) return null;
  const filtered = activeLibraryFolder.value !== null;
  return (
    <section class="mt-8 space-y-3">
      <header class="flex items-center justify-between gap-3 border-b border-border pb-2">
        <h2 class="flex items-center gap-2 text-base font-bold">
          <PenSquare className="size-4 text-violet-500" />
          {filtered ? "Redações nesta pasta" : "Redações"}
          <span class="rounded-full bg-violet-500/15 px-2 py-0.5 text-[10px] font-semibold text-violet-700 dark:text-violet-300">
            {list.length}
          </span>
        </h2>
        <p class="text-[11px] text-muted-foreground">
          Click pra aplicar o tema e abrir no Estudar com IA.
        </p>
      </header>
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {list.map((e) => (
          <DiscoverEssayCard key={e.id} essay={e} />
        ))}
      </div>
    </section>
  );
}

function DiscoverEssayCard({ essay: e }: { essay: DiscoverEssayRow }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function apply() {
    setBusy(true);
    setErr(null);
    try {
      const r = await call<{ ok: true; sessionId: string }>(
        `/api/student/essay-themes?id=${encodeURIComponent(e.id)}&action=apply`,
        { method: "POST", body: "{}" }
      );
      activeSessionId.value = r.sessionId;
      activeNav.value = "aistudy";
    } catch (err) {
      setErr(friendlyError(err));
      setBusy(false);
    }
  }

  const dateLabel = e.applicationDate
    ? new Date(e.applicationDate).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      })
    : e.year != null
      ? String(e.year)
      : "";

  return (
    <button
      type="button"
      onClick={() => void apply()}
      disabled={busy}
      class="group flex flex-col items-stretch gap-2 rounded-md border border-violet-400/30 bg-violet-500/5 px-3 py-3 text-left transition-colors hover:border-violet-500/60 hover:bg-violet-500/10 disabled:opacity-50"
    >
      <div class="flex items-start justify-between gap-2">
        <div class="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">
          <PenSquare className="size-3" />
          Redação · {e.banca}
        </div>
        {busy ? (
          <Loader2 className="size-3.5 animate-spin text-violet-500" />
        ) : (
          <Sparkles className="size-3.5 text-violet-400 opacity-0 transition-opacity group-hover:opacity-100" />
        )}
      </div>
      <h3 class="line-clamp-2 text-sm font-bold leading-tight">
        {e.displayName || e.title}
      </h3>
      <div class="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
        {dateLabel && (
          <span class="inline-flex items-center gap-1">
            <Calendar className="size-3" /> {dateLabel}
          </span>
        )}
        {e.phase && <span>{e.phase}</span>}
        {e.edition && <span class="opacity-70">· {e.edition}</span>}
      </div>
      {err && (
        <span class="text-[10px] text-destructive">
          <AlertCircle className="mr-1 inline size-3" /> {err}
        </span>
      )}
    </button>
  );
}

function SearchBox() {
  const q = bankSearchQuery.value;
  return (
    <div class="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
      <input
        type="search"
        value={q}
        onInput={(e) =>
          (bankSearchQuery.value = (e.target as HTMLInputElement).value)
        }
        placeholder="Buscar banco por nome, descrição…"
        aria-label="Buscar banco na Biblioteca"
        class="w-full rounded-md border border-border bg-background py-2 pl-9 pr-9 text-sm outline-none focus:border-primary"
      />
      {q && (
        <button
          type="button"
          onClick={() => (bankSearchQuery.value = "")}
          aria-label="Limpar busca"
          title="Limpar busca"
          class="absolute right-2 top-1/2 grid size-7 -translate-y-1/2 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <X className="size-3.5" />
        </button>
      )}
    </div>
  );
}

// Removido: chips horizontais de pastas. Substituídos pela árvore lateral
// (FolderTreeAside) que replica a UX de File Explorer da admin.
// [[project-biblioteca-de-provas]]
function _LibraryFolderChipsLegacy() {
  return null;
}

/** Seção de tópicos: por padrão fica COLAPSADA — apenas um botão "Tópicos"
 *  que expande a lista. Pedido 2026-05-19: não poluir a tela com chips de
 *  tópicos por padrão. Quando há um filtro ativo, mostra também um chip de
 *  remoção rápida (X) sem precisar expandir. */
function TopicsSection() {
  const list = topics.value;
  if (list.length === 0) return null;
  const active = activeTopic.value;
  const expanded = topicsExpanded.value || active !== null;
  return (
    <div class="space-y-1.5">
      <div class="flex flex-wrap items-center gap-1.5 text-[11px]">
        <button
          type="button"
          onClick={() => (topicsExpanded.value = !topicsExpanded.value)}
          class={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 font-semibold transition-colors ${
            expanded
              ? "border-primary/40 bg-primary/10 text-primary"
              : "border-border bg-card/60 text-muted-foreground hover:border-primary/40 hover:text-foreground"
          }`}
          {...(expanded ? { "aria-expanded": "true" as const } : { "aria-expanded": "false" as const })}
        >
          <Tag className="size-3" />
          Tópicos
          <span class="rounded-full bg-muted/60 px-1.5 text-[9px] font-bold text-muted-foreground">
            {list.length}
          </span>
        </button>
        {active && (
          <button
            type="button"
            onClick={() => (activeTopic.value = null)}
            class="inline-flex items-center gap-1 rounded-full border border-violet-500/40 bg-violet-500/10 px-2.5 py-1 text-[11px] font-semibold text-violet-700 hover:bg-violet-500/15 dark:text-violet-300"
            title="Remover filtro de tópico"
          >
            {active}
            <X className="size-3" />
          </button>
        )}
      </div>
      {expanded && (
        <div class="flex flex-wrap items-center gap-1.5">
          <Chip
            label="Todos"
            active={active === null}
            onClick={() => (activeTopic.value = null)}
          />
          {list.map((t) => {
            const isActive = active === t.topic;
            return (
              <Chip
                key={t.topic}
                label={t.topic}
                count={t.count}
                active={isActive}
                onClick={() => (activeTopic.value = isActive ? null : t.topic)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Árvore de pastas lateral — replica a UX de File Explorer da admin. Cada
 *  pasta mostra o contador. Subpastas colapsáveis (estado em localStorage). */
function FolderTreeAside() {
  const all = libraryFolders.value;
  const total = items.value.length;
  const noFolderCount = all.length === 0 ? null : null;
  return (
    <aside class="rounded-md border border-border bg-card/40">
      <header class="border-b border-border px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
        Pastas
      </header>
      <div class="max-h-[60vh] overflow-y-auto py-1.5">
        <TreeButton
          label="Todas"
          icon={Library}
          active={activeLibraryFolder.value === null}
          count={total > 0 ? total : undefined}
          onClick={() => (activeLibraryFolder.value = null)}
        />
        <FolderTreeBranch parentId={null} depth={0} />
        <div class="mt-1 border-t border-border pt-1">
          <TreeButton
            label="Sem categoria"
            icon={FolderClosed}
            active={activeLibraryFolder.value === "no-folder"}
            count={noFolderCount ?? undefined}
            muted
            onClick={() => (activeLibraryFolder.value = "no-folder")}
          />
        </div>
      </div>
    </aside>
  );
}

// Grid de subpastas exibida no main content quando o aluno entra numa pasta
// da biblioteca. Mantém a paridade com o admin LibraryExplorerView: quem
// clica numa pasta vê seus filhos antes da lista de bancos.
function SubfoldersGrid() {
  const active = activeLibraryFolder.value;
  // "no-folder" (órfãos) e null (raiz) não têm subpastas semânticas.
  if (active === null || active === "no-folder") return null;
  const children = libraryFolders.value
    .filter((f) => f.parentId === active)
    .sort(
      (a, b) =>
        (a.position || 0) - (b.position || 0) || a.name.localeCompare(b.name)
    );
  if (children.length === 0) return null;
  return (
    <section
      class="rounded-md border border-border bg-card/40 p-3"
      aria-label="Subpastas"
    >
      <header class="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        <FolderClosed className="size-3 text-amber-500" />
        Subpastas ({children.length})
      </header>
      <ul class="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
        {children.map((f) => (
          <li key={f.id}>
            <button
              type="button"
              onClick={() => {
                activeLibraryFolder.value = f.id;
                // Expande no tree lateral pra refletir a entrada na subpasta.
                const next = new Set(treeExpanded.value);
                next.add(active);
                treeExpanded.value = next;
                persistTreeExpanded();
              }}
              class="flex w-full items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-left text-sm transition-colors hover:border-primary/50 hover:bg-accent"
              title={`Abrir pasta "${f.name}"`}
            >
              <FolderClosed className="size-5 shrink-0 text-amber-500" />
              <div class="min-w-0 flex-1">
                <div class="truncate font-semibold">{f.name}</div>
                {(f.bankCount > 0 || f.essayCount > 0) && (
                  <div class="text-[10px] text-muted-foreground">
                    {[
                      f.bankCount > 0 ? `${f.bankCount} ${f.bankCount === 1 ? "banco" : "bancos"}` : null,
                      f.essayCount > 0 ? `${f.essayCount} ${f.essayCount === 1 ? "redação" : "redações"}` : null
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                )}
              </div>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function FolderTreeBranch({
  parentId,
  depth
}: {
  parentId: string | null;
  depth: number;
}) {
  const children = libraryFolders.value
    .filter((f) => (f.parentId ?? null) === parentId)
    .sort((a, b) => (a.position || 0) - (b.position || 0) || a.name.localeCompare(b.name));
  if (children.length === 0) return null;
  return (
    <ul>
      {children.map((f) => (
        <FolderTreeItem key={f.id} folder={f} depth={depth} />
      ))}
    </ul>
  );
}

function FolderTreeItem({
  folder,
  depth
}: {
  folder: LibraryFolderRow;
  depth: number;
}) {
  const hasChildren = libraryFolders.value.some((f) => f.parentId === folder.id);
  const isExpanded = treeExpanded.value.has(folder.id);
  const isActive = activeLibraryFolder.value === folder.id;
  function toggle(e: MouseEvent) {
    e.stopPropagation();
    const next = new Set(treeExpanded.value);
    if (next.has(folder.id)) next.delete(folder.id);
    else next.add(folder.id);
    treeExpanded.value = next;
    persistTreeExpanded();
  }
  function select() {
    activeLibraryFolder.value = folder.id;
    if (hasChildren && !isExpanded) {
      const next = new Set(treeExpanded.value);
      next.add(folder.id);
      treeExpanded.value = next;
      persistTreeExpanded();
    }
  }
  return (
    <li>
      <div
        class={`flex items-center gap-1 px-1.5 py-1 text-sm transition-colors ${
          isActive ? "bg-primary/10 font-semibold text-primary" : "hover:bg-accent"
        }`}
        style={{ paddingLeft: `${0.5 + depth * 1}rem` }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={toggle}
            class="grid size-4 shrink-0 place-items-center text-muted-foreground hover:text-foreground"
            aria-label={isExpanded ? "Recolher" : "Expandir"}
          >
            <ArrowRight
              className={`size-3 transition-transform ${isExpanded ? "rotate-90" : ""}`}
            />
          </button>
        ) : (
          <span class="size-4 shrink-0" aria-hidden="true" />
        )}
        <button
          type="button"
          onClick={select}
          class="flex flex-1 items-center gap-1.5 truncate text-left"
        >
          {isExpanded ? (
            <FolderOpen className="size-4 shrink-0 text-amber-500" />
          ) : (
            <FolderClosed className="size-4 shrink-0 text-amber-500" />
          )}
          <span class="flex-1 truncate">{folder.name}</span>
          {(folder.bankCount > 0 || folder.essayCount > 0) && (
            <span
              class="shrink-0 rounded bg-muted px-1.5 font-mono text-[10px] text-muted-foreground"
              title={`${folder.bankCount} banco(s) + ${folder.essayCount} redação(ões)`}
            >
              {folder.bankCount + folder.essayCount}
            </span>
          )}
        </button>
      </div>
      {hasChildren && isExpanded && (
        <FolderTreeBranch parentId={folder.id} depth={depth + 1} />
      )}
    </li>
  );
}

function TreeButton({
  label,
  icon: Icon,
  active,
  count,
  muted,
  onClick
}: {
  label: string;
  icon: typeof Library;
  active: boolean;
  count?: number;
  muted?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors ${
        active
          ? "bg-primary/10 font-semibold text-primary"
          : muted
          ? "text-muted-foreground hover:bg-accent hover:text-foreground"
          : "hover:bg-accent"
      }`}
    >
      <Icon className="size-4 shrink-0" />
      <span class="flex-1 truncate">{label}</span>
      {typeof count === "number" && count > 0 && (
        <span class="shrink-0 rounded bg-muted px-1.5 font-mono text-[10px] text-muted-foreground">
          {count}
        </span>
      )}
    </button>
  );
}

function Chip({
  label,
  count,
  active,
  icon: Icon,
  onClick
}: {
  label: string;
  count?: number;
  active: boolean;
  icon?: typeof FolderClosed;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-medium transition-colors ${
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-card/60 text-muted-foreground hover:border-primary/40 hover:text-foreground"
      }`}
    >
      {Icon && <Icon className="size-3.5" />}
      {label}
      {typeof count === "number" && count > 0 && (
        <span
          class={`rounded-full px-1.5 text-[9px] font-bold ${
            active
              ? "bg-primary-foreground text-primary"
              : "bg-muted text-muted-foreground"
          }`}
        >
          {count}
        </span>
      )}
    </button>
  );
}

// Empty state com guidance — distingue "filtros restritivos" de "Biblioteca
// vazia mesmo" e oferece CTA correto pra cada caso. Padrão visual do admin.
function DiscoverEmptyState() {
  const hasSearch = !!bankSearchQuery.value;
  const hasTopic = !!activeTopic.value;
  const hasFolder = !!activeLibraryFolder.value;
  const filteringSomething = hasSearch || hasTopic || hasFolder;
  return (
    <div class="rounded-md border border-dashed border-border bg-background/40 px-4 py-12 text-center">
      <Database className="mx-auto mb-3 size-8 text-muted-foreground" />
      {filteringSomething ? (
        <>
          <p class="text-sm font-semibold">Nenhum banco bate com esses filtros.</p>
          <p class="mt-1 text-xs text-muted-foreground">
            Tente uma palavra-chave mais geral{hasTopic && " ou outro tópico"}
            {hasFolder && " ou outra pasta"}.
          </p>
          <div class="mt-3 inline-flex flex-wrap items-center justify-center gap-2">
            {hasSearch && (
              <button
                type="button"
                onClick={() => (bankSearchQuery.value = "")}
                class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
              >
                <X className="size-3.5" /> Limpar busca
              </button>
            )}
            {hasTopic && (
              <button
                type="button"
                onClick={() => (activeTopic.value = null)}
                class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
              >
                <X className="size-3.5" /> Limpar tópico
              </button>
            )}
            {hasFolder && (
              <button
                type="button"
                onClick={() => (activeLibraryFolder.value = null)}
                class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
              >
                <X className="size-3.5" /> Voltar para a raiz
              </button>
            )}
          </div>
        </>
      ) : (
        <>
          <p class="text-sm font-semibold">
            A Biblioteca de Provas ainda está vazia
          </p>
          <p class="mt-1 text-xs text-muted-foreground">
            Quando os admins publicarem material curado, ele aparece aqui.
            Enquanto isso, você pode subir o seu próprio banco em{" "}
            <strong>Meu acervo</strong>.
          </p>
          <button
            type="button"
            onClick={() => (activeNav.value = "bancos")}
            class="mt-3 inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/10"
          >
            <ArrowRight className="size-3.5" />
            Ir para Meu acervo
          </button>
        </>
      )}
    </div>
  );
}

// Linha compacta — usada no modo list. Reusa o mesmo BankDetailModal.
function DiscoverListRow({ bank }: { bank: DiscoverBankRow }) {
  const [detailOpen, setDetailOpen] = useState(false);
  const [imported, setImported] = useState(bank.alreadyImported);
  const [importedCopyId, setImportedCopyId] = useState<string | null>(
    bank.myCopyId
  );
  const accessibleSomehow = imported || bank.alreadyAccessible;
  return (
    <li>
      <button
        type="button"
        onClick={() => setDetailOpen(true)}
        class="flex w-full items-center gap-3 rounded-md border border-border bg-card/40 px-3 py-2 text-left transition-colors hover:border-primary/40"
      >
        <Database className="size-4 shrink-0 text-primary" />
        <div class="min-w-0 flex-1">
          <div class="flex flex-wrap items-baseline gap-2">
            <span class="truncate text-sm font-bold">{bank.name}</span>
            {accessibleSomehow && (
              <span class="inline-flex items-center gap-0.5 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
                <CheckCircle2 className="size-2.5" />
                {imported ? "no acervo" : "acessível"}
              </span>
            )}
          </div>
          {bank.description && (
            <div class="truncate text-[11px] text-muted-foreground">
              {bank.description}
            </div>
          )}
        </div>
        <div class="hidden items-center gap-1.5 text-[10px] sm:flex">
          <span class="inline-flex items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 font-semibold text-primary">
            {bank.questionCount}
            <span class="opacity-70">
              {bank.questionCount === 1 ? "questão" : "questões"}
            </span>
          </span>
          {typeof bank.mcCount === "number" && bank.mcCount > 0 && (
            <span class="rounded bg-sky-500/10 px-1.5 py-0.5 font-semibold text-sky-700 dark:text-sky-300">
              {bank.mcCount} ME
            </span>
          )}
          {typeof bank.essayCount === "number" && bank.essayCount > 0 && (
            <span class="rounded bg-fuchsia-500/10 px-1.5 py-0.5 font-semibold text-fuchsia-700 dark:text-fuchsia-300">
              {bank.essayCount} DISS
            </span>
          )}
          {(bank.dissertativeCount ?? 0) > 0 && (
            <span
              class="inline-flex items-center gap-0.5 rounded bg-fuchsia-500/15 px-1.5 py-0.5 font-bold uppercase tracking-wider text-fuchsia-700 ring-1 ring-fuchsia-500/30 dark:text-fuchsia-300"
              title="Banco com versão dissertativa gerada por IA"
            >
              <Sparkles className="size-3" />
              IA Disc.
            </span>
          )}
        </div>
        <ArrowRight className="size-4 shrink-0 text-muted-foreground/60" />
      </button>
      {detailOpen && (
        <BankDetailModal
          bank={bank}
          imported={imported}
          importedCopyId={importedCopyId}
          onClose={() => setDetailOpen(false)}
          onOpenInAcervo={() => {
            setDetailOpen(false);
            if (importedCopyId) {
              activeNav.value = "bancos";
              openBank(importedCopyId);
            } else if (bank.alreadyAccessible) {
              activeNav.value = "bancos";
              openBank(bank.id);
            }
          }}
          onImport={() => {
            // O fluxo de import vive no card grid. Pra simplificar a lista
            // compacta, abrimos o detalhe e o admin clica "Importar" de lá.
            // (Mantém a mesma UX final, sem duplicar lógica de folder picker.)
            setDetailOpen(false);
            void call<{ bank: { id: string; name: string } }>(
              `/api/student/discover/banks?id=${encodeURIComponent(bank.id)}`,
              { method: "POST", body: JSON.stringify({ folderId: null }) }
            )
              .then((res) => {
                setImported(true);
                setImportedCopyId(res.bank.id);
                void loadBanks({ silent: true });
              })
              .catch(() => {/* surfaceia via toast futuro; row é compacta */});
          }}
        />
      )}
    </li>
  );
}

function DiscoverCard({ bank }: { bank: DiscoverBankRow }) {
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState(bank.alreadyImported);
  const [importedCopyId, setImportedCopyId] = useState<string | null>(bank.myCopyId);
  const [err, setErr] = useState<string | null>(null);
  const [pickingFolder, setPickingFolder] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  async function doImport(destFolderId: string | null) {
    setPickingFolder(false);
    setImporting(true);
    setErr(null);
    try {
      const res = await call<{
        bank: { id: string; name: string };
        alreadyImported?: boolean;
      }>(`/api/student/discover/banks?id=${encodeURIComponent(bank.id)}`, {
        method: "POST",
        body: JSON.stringify({ folderId: destFolderId })
      });
      setImported(true);
      setImportedCopyId(res.bank.id);
      void loadBanks({ silent: true });
    } catch (err) {
      setErr(friendlyError(err));
    } finally {
      setImporting(false);
    }
  }

  function openMyCopy() {
    if (!importedCopyId) return;
    // Navega pra Bancos e abre o detalhe.
    activeNav.value = "bancos";
    openBank(importedCopyId);
  }

  return (
    <article
      class="relative flex flex-col rounded-lg border border-border bg-card/40 p-4 transition-colors hover:border-primary/40 cursor-pointer"
      onClick={(e) => {
        // Não abre o modal se clicou em um botão (ações primárias).
        if ((e.target as HTMLElement).closest("button, a")) return;
        setDetailOpen(true);
      }}
      title="Clique para ver detalhes"
    >
      <h3 class="line-clamp-2 text-base font-bold leading-snug">{bank.name}</h3>
      {bank.description && (
        <p class="mt-1 line-clamp-3 text-xs text-muted-foreground">
          {bank.description}
        </p>
      )}
      {detailOpen && (
        <BankDetailModal
          bank={bank}
          imported={imported}
          importedCopyId={importedCopyId}
          onClose={() => setDetailOpen(false)}
          onOpenInAcervo={() => {
            setDetailOpen(false);
            if (importedCopyId) {
              activeNav.value = "bancos";
              openBank(importedCopyId);
            } else if (bank.alreadyAccessible) {
              activeNav.value = "bancos";
              openBank(bank.id);
            }
          }}
          onImport={() => {
            setDetailOpen(false);
            setPickingFolder(true);
          }}
        />
      )}
      <div class="mt-2 flex flex-wrap items-center gap-1.5 text-[10px]">
        <span class="inline-flex items-center gap-1 rounded-md bg-primary/10 px-1.5 py-0.5 font-semibold text-primary">
          <Database className="size-3" />
          {bank.questionCount} {bank.questionCount === 1 ? "questão" : "questões"}
        </span>
        {typeof bank.mcCount === "number" && bank.mcCount > 0 && (
          <span
            class="inline-flex items-center gap-0.5 rounded-md bg-sky-500/10 px-1.5 py-0.5 font-semibold text-sky-700 dark:text-sky-300"
            title="Questões de múltipla escolha"
          >
            {bank.mcCount} ME
          </span>
        )}
        {typeof bank.essayCount === "number" && bank.essayCount > 0 && (
          <span
            class="inline-flex items-center gap-0.5 rounded-md bg-fuchsia-500/10 px-1.5 py-0.5 font-semibold text-fuchsia-700 dark:text-fuchsia-300"
            title="Questões dissertativas"
          >
            {bank.essayCount} DISS
          </span>
        )}
        {(bank.dissertativeCount ?? 0) > 0 && (
          <span
            class="inline-flex items-center gap-0.5 rounded-md bg-fuchsia-500/15 px-1.5 py-0.5 font-bold uppercase tracking-wider text-fuchsia-700 ring-1 ring-fuchsia-500/30 dark:text-fuchsia-300"
            title={`Banco com versão dissertativa gerada por IA: ${bank.dissertativeCount} questão${bank.dissertativeCount !== 1 ? "ões" : ""}`}
          >
            <Sparkles className="size-3" />
            IA Disc.
          </span>
        )}
        {bank.tags.slice(0, 4).map((t) => (
          <span
            key={t}
            class="rounded-md bg-violet-500/10 px-1.5 py-0.5 font-medium text-violet-700 dark:text-violet-300"
          >
            {t}
          </span>
        ))}
      </div>

      {err && (
        <p class="mt-2 flex items-center gap-1.5 text-[11px] text-destructive">
          <AlertCircle className="size-3" /> {err}
        </p>
      )}

      <div class="mt-3 flex">
        {imported ? (
          <button
            type="button"
            onClick={openMyCopy}
            disabled={!importedCopyId}
            className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-500/10 disabled:opacity-50 dark:text-emerald-300"
          >
            <CheckCircle2 className="size-3.5" /> Já importado
            <ArrowRight className="size-3" />
          </button>
        ) : bank.alreadyAccessible ? (
          <button
            type="button"
            onClick={() => openBank(bank.id)}
            title={
              bank.accessReason === "owner"
                ? "Você é o dono deste banco"
                : bank.accessReason === "company"
                ? "Disponível pela sua organização"
                : bank.accessReason === "grant"
                ? "Compartilhado com você"
                : "Já no seu acervo"
            }
            className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-500/10 dark:text-emerald-300"
          >
            <CheckCircle2 className="size-3.5" /> Já no seu acervo
            <ArrowRight className="size-3" />
          </button>
        ) : pickingFolder ? (
          <FolderPicker
            onPick={(folderId) => void doImport(folderId)}
            onCancel={() => setPickingFolder(false)}
          />
        ) : (
          <button
            type="button"
            onClick={() => setPickingFolder(true)}
            disabled={importing}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:brightness-110 disabled:opacity-50"
          >
            {importing ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Download className="size-3.5" />
            )}
            Importar pro meu acervo
          </button>
        )}
      </div>
    </article>
  );
}

function FolderPicker({
  onPick,
  onCancel
}: {
  onPick: (folderId: string | null) => void;
  onCancel: () => void;
}) {
  const folders = foldersData.value;
  return (
    <div class="w-full rounded-md border border-primary/40 bg-primary/5 p-2">
      <p class="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-primary">
        Em qual pasta do seu acervo?
      </p>
      <div class="space-y-1">
        <button
          type="button"
          onClick={() => onPick(null)}
          className="flex w-full items-center gap-1.5 rounded px-2 py-1 text-left text-xs hover:bg-accent"
        >
          <FolderOpen className="size-3.5 text-muted-foreground" />
          Sem pasta (raiz)
        </button>
        {folders.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => onPick(f.id)}
            className="flex w-full items-center gap-1.5 rounded px-2 py-1 text-left text-xs hover:bg-accent"
          >
            <FolderClosed className="size-3.5 text-muted-foreground" />
            {f.name}
          </button>
        ))}
        {folders.length === 0 && (
          <p class="px-2 py-1 text-[10px] text-muted-foreground">
            Você ainda não criou pastas pessoais. Vai pra raiz por padrão.
          </p>
        )}
      </div>
      <div class="mt-2 flex justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-border bg-background px-2 py-0.5 text-[11px] font-medium hover:bg-accent"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}

/** Modal de detalhe do banco na Biblioteca. Mostra nome, descrição, contagens
 *  (total, ME, DISS), tags e ações de "Ir para o banco" ou "Importar".
 *  [[project-biblioteca-de-provas]] pedido 2026-05-19. */
function BankDetailModal({
  bank,
  imported,
  importedCopyId,
  onClose,
  onOpenInAcervo,
  onImport
}: {
  bank: DiscoverBankRow;
  imported: boolean;
  importedCopyId: string | null;
  onClose: () => void;
  onOpenInAcervo: () => void;
  onImport: () => void;
}) {
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // ESC fecha a prévia primeiro (modal sobre modal). Só o BankDetail
      // captura ESC se não há prévia aberta.
      if (e.key === "Escape" && !previewOpen) onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, previewOpen]);

  const accessibleSomehow = imported || bank.alreadyAccessible;
  const hasCopyToOpen = importedCopyId || (bank.alreadyAccessible && !imported);

  return (
    <div
      class="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-3 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Detalhes do banco"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div class="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl">
        <header class="flex items-start justify-between gap-2 border-b border-border px-5 py-3">
          <div class="min-w-0 flex-1">
            <div class="text-[10px] font-bold uppercase tracking-wider text-violet-600 dark:text-violet-400">
              Banco da biblioteca
            </div>
            <h2 class="mt-0.5 text-base font-bold leading-snug">{bank.name}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            class="grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-accent"
            aria-label="Fechar"
          >
            <X className="size-4" />
          </button>
        </header>

        <div class="flex-1 overflow-y-auto p-5">
          {bank.description && (
            <p class="text-sm leading-relaxed text-muted-foreground">{bank.description}</p>
          )}

          <div class="mt-4 grid grid-cols-3 gap-2">
            <KpiBox
              label="Total"
              value={String(bank.questionCount)}
              hint={bank.questionCount === 1 ? "questão" : "questões"}
              tone="primary"
            />
            <KpiBox
              label="ME"
              value={String(bank.mcCount ?? 0)}
              hint="múltipla escolha"
              tone="sky"
            />
            <KpiBox
              label="DISS"
              value={String(bank.essayCount ?? 0)}
              hint="dissertativas"
              tone="fuchsia"
            />
          </div>

          {bank.tags.length > 0 && (
            <div class="mt-4">
              <div class="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Tópicos
              </div>
              <div class="flex flex-wrap gap-1">
                {bank.tags.map((t) => (
                  <span
                    key={t}
                    class="rounded-md bg-violet-500/10 px-1.5 py-0.5 text-[10px] font-medium text-violet-700 dark:text-violet-300"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}

          {accessibleSomehow && (
            <div class="mt-4 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-300">
              <CheckCircle2 className="mr-1 inline size-3.5" />
              {imported
                ? "Esse banco já está no seu acervo (cópia particular)"
                : bank.accessReason === "owner"
                ? "Você é o dono deste banco"
                : bank.accessReason === "company"
                ? "Disponível pela sua organização"
                : bank.accessReason === "grant"
                ? "Compartilhado com você"
                : "Já está no seu acervo"}
            </div>
          )}
        </div>

        <footer class="flex flex-wrap items-center justify-end gap-2 border-t border-border px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-sm font-semibold hover:bg-accent"
          >
            Fechar
          </button>
          {/* Pré-visualizar questões — abre modal secundário com navegação
              1-a-1, sem marcar alternativa. [[project-library-bank-preview-questions]] */}
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            class="inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/5 px-3 py-1.5 text-sm font-semibold text-primary hover:bg-primary/10"
            title="Ver as questões deste banco antes de importar"
          >
            <Eye className="size-4" />
            Pré-visualizar
          </button>
          {hasCopyToOpen ? (
            <button
              type="button"
              onClick={onOpenInAcervo}
              class="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-4 py-1.5 text-sm font-bold text-white hover:bg-emerald-700"
            >
              <ArrowRight className="size-4" />
              Ir para o banco no acervo
            </button>
          ) : (
            <button
              type="button"
              onClick={onImport}
              class="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-bold text-primary-foreground hover:brightness-110"
            >
              <Download className="size-4" />
              Importar para o meu acervo
            </button>
          )}
        </footer>
      </div>
      {previewOpen && (
        <PreviewQuestionsModal
          bankId={bank.id}
          bankName={bank.name}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
}

// =========================================================================
// PreviewQuestionsModal — modal sobre modal: aluno navega 1-a-1 pelas
// questões do banco da Biblioteca antes de decidir importar.
// [[project-library-bank-preview-questions]]
// =========================================================================
function PreviewQuestionsModal({
  bankId,
  bankName,
  onClose
}: {
  bankId: string;
  bankName: string;
  onClose: () => void;
}) {
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [questoes, setQuestoes] = useState<PreviewQuestion[]>([]);
  const [total, setTotal] = useState(0);
  const [capped, setCapped] = useState(false);
  const [idx, setIdx] = useState(0);
  const [showGabarito, setShowGabarito] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await call<{
          questoes: PreviewQuestion[];
          totalQuestoes: number;
          capped: boolean;
        }>(`/api/student/discover/banks?action=preview&id=${encodeURIComponent(bankId)}`);
        if (cancelled) return;
        setQuestoes(res.questoes ?? []);
        setTotal(res.totalQuestoes ?? 0);
        setCapped(!!res.capped);
        setPhase("ready");
      } catch (err) {
        if (cancelled) return;
        setErrMsg(friendlyError(err));
        setPhase("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bankId]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") setIdx((i) => Math.max(0, i - 1));
      else if (e.key === "ArrowRight")
        setIdx((i) => Math.min(questoes.length - 1, i + 1));
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, questoes.length]);

  const q = questoes[idx];
  const safeIdx = questoes.length === 0 ? 0 : Math.min(idx, questoes.length - 1);

  return (
    <div
      class="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-3 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Pré-visualização de questões"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div class="flex h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl">
        <header class="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-border px-5 py-3">
          <div class="min-w-0 flex-1">
            <div class="text-[10px] font-bold uppercase tracking-wider text-primary">
              Pré-visualizar
            </div>
            <h2 class="mt-0.5 truncate text-base font-bold">{bankName}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            class="grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-accent"
            aria-label="Fechar prévia"
          >
            <X className="size-4" />
          </button>
        </header>

        {/* Nav + toggle gabarito */}
        {phase === "ready" && questoes.length > 0 && (
          <div class="flex shrink-0 flex-wrap items-center gap-2 border-b border-border bg-card/60 px-5 py-2 text-xs">
            <button
              type="button"
              onClick={() => setIdx(Math.max(0, safeIdx - 1))}
              disabled={safeIdx === 0}
              class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 font-medium hover:border-primary disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Questão anterior"
            >
              <ChevronLeft className="size-3" /> Anterior
            </button>
            <div class="inline-flex items-center gap-1">
              <span class="text-muted-foreground">Questão</span>
              <input
                type="number"
                min={1}
                max={questoes.length}
                value={safeIdx + 1}
                onInput={(e) => {
                  const n = Number((e.target as HTMLInputElement).value);
                  if (Number.isFinite(n)) {
                    setIdx(Math.min(Math.max(0, n - 1), questoes.length - 1));
                  }
                }}
                aria-label="Ir para a questão"
                class="w-14 rounded-md border border-border bg-background px-1.5 py-0.5 text-center text-xs font-bold outline-none focus:border-primary"
              />
              <span class="text-muted-foreground">de {questoes.length}</span>
            </div>
            <button
              type="button"
              onClick={() => setIdx(Math.min(questoes.length - 1, safeIdx + 1))}
              disabled={safeIdx >= questoes.length - 1}
              class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 font-medium hover:border-primary disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Próxima questão"
            >
              Próxima <ChevronRight className="size-3" />
            </button>
            <span class="ml-auto inline-flex items-center gap-2">
              <label class="inline-flex cursor-pointer items-center gap-1.5 text-[11px] text-muted-foreground">
                <input
                  type="checkbox"
                  checked={showGabarito}
                  onChange={(e) =>
                    setShowGabarito((e.target as HTMLInputElement).checked)
                  }
                  class="size-3.5 accent-primary"
                />
                Mostrar gabarito
              </label>
              {capped && (
                <span class="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300">
                  Mostrando 50 de {total}
                </span>
              )}
            </span>
          </div>
        )}

        {/* Conteúdo */}
        <div class="min-h-0 flex-1 overflow-y-auto p-5">
          {phase === "loading" && (
            <p class="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              Carregando questões…
            </p>
          )}
          {phase === "error" && (
            <div class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              <AlertCircle className="mr-2 inline size-4" />
              {errMsg ?? "Falha ao carregar prévia."}
            </div>
          )}
          {phase === "ready" && questoes.length === 0 && (
            <p class="py-12 text-center text-sm text-muted-foreground">
              Este banco ainda não tem questões publicadas.
            </p>
          )}
          {phase === "ready" && q && (
            <QuestionPreview q={q} showGabarito={showGabarito} />
          )}
        </div>

        <footer class="flex shrink-0 items-center justify-end border-t border-border px-5 py-3 text-[11px] text-muted-foreground">
          Use ← → para navegar · Esc para fechar
        </footer>
      </div>
    </div>
  );
}

function KpiBox({
  label,
  value,
  hint,
  tone
}: {
  label: string;
  value: string;
  hint: string;
  tone: "primary" | "sky" | "fuchsia";
}) {
  const cls =
    tone === "primary"
      ? "border-primary/30 bg-primary/5 text-primary"
      : tone === "sky"
      ? "border-sky-500/30 bg-sky-500/5 text-sky-700 dark:text-sky-300"
      : "border-fuchsia-500/30 bg-fuchsia-500/5 text-fuchsia-700 dark:text-fuchsia-300";
  return (
    <div class={`rounded-md border px-2.5 py-2 ${cls}`}>
      <div class="text-[10px] font-bold uppercase tracking-wider opacity-80">{label}</div>
      <div class="mt-0.5 text-xl font-bold tabular-nums">{value}</div>
      <div class="text-[10px] opacity-70">{hint}</div>
    </div>
  );
}
